import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["centered", "children", "use", "headerCallback", "size"];
// temporary until migration is complete
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import { toPx } from '../../../utils/toPx';
import { DialogContext, DialogContextProvider } from '../Dialog/DialogContext';
import { StyledHeaderInner } from '../DialogHeader/DialogHeader';
import emptyFunction from 'react-utils/emptyFunction';
import { StyledDialogBody } from '../DialogBody/DialogBody';
import { ConfirmDialogContext } from '../ConfirmDialog/ConfirmDialogContext';
import { getBackwardsCompatibilityClasses } from './internals/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
const GRID_BREAKPOINT_SMALL = '544px';
const ABOVE_MOBILE_BREAKPOINT = toPx(parseInt(GRID_BREAKPOINT_SMALL, 10) + 1);
const FullscreenStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  borderRadius: 0
};
const SidebarStyles = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  borderRadius: 0,
  minWidth: '300px',
  maxWidth: '100%'
};
const LightboxStyles = {
  bottom: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'block',
  height: '100%',
  overflow: 'hidden',
  width: '100%'
};
const sizeToHeightMap = {
  auto: undefined,
  medium: 408,
  short: 250,
  tall: 700
};
export const getDialogHeaderBackground = ({
  theme,
  use
}) => {
  const isConversational = use === 'conversational';
  const isDanger = use === 'danger';
  const isUpgrades = use === 'upgrades';
  const isLightbox = use === 'lightbox';
  const isFullscreen = use === 'fullscreen';
  const getBackgroundColor = () => {
    if (isLightbox) {
      return theme.color['fill-transparent-default'];
    }
    if (!isConversational && !isFullscreen) {
      return theme.color['temp-do-not-use-fill-secondary-default'];
    }
    return theme.color['fill-surface-default'];
  };
  const getBackgroundImage = () => {
    if (isDanger) {
      return `repeating-linear-gradient(135deg,${theme.color['fill-alert-default']},${theme.color['fill-alert-default']} 24px,${theme.color['fill-alert-hover']} 0,${theme.color['fill-alert-hover']} 48px)`;
    }
    if (isUpgrades) {
      return `linear-gradient(-180deg, ${theme.color['fill-accent-orange-default']} 20%, ${theme.color['fill-accent-orange-default']} 71%)`;
    }
    if (isLightbox || isFullscreen) {
      return undefined;
    }
    if (!isConversational) {
      return `linear-gradient(-303deg, ${theme.color['temp-do-not-use-fill-secondary-default']}, ${theme.color['fill-positive-default']})`;
    }
    return undefined;
  };
  return {
    backgroundColor: getBackgroundColor(),
    backgroundImage: getBackgroundImage()
  };
};
export const StyledDialog = styled.div.withConfig({
  displayName: "Dialog__StyledDialog",
  componentId: "rnmck1-0"
})(({
  theme,
  _dialogWidth,
  headerOverflow,
  $centered,
  $size,
  $cssStyles
}) => {
  const modalUse = useContext(DialogContext).use;
  const isConfirmDialog = useContext(ConfirmDialogContext).isConfirmDialog;
  const isDefault = modalUse === 'default';
  const isFullscreen = modalUse === 'fullscreen';
  const isSidebar = modalUse === 'sidebar';
  const isLightbox = modalUse === 'lightbox';
  return Object.assign({
    textAlign: $centered ? 'center' : undefined,
    justifyContent: $centered ? 'center' : undefined,
    [`${StyledHeaderInner}`]: {
      justifyContent: $centered ? 'center !important' : undefined
    },
    [`${StyledDialogBody}`]: Object.assign({}, $size && {
      minHeight: sizeToHeightMap[$size]
    }),
    borderRadius: theme.borderRadius['transitional-floating'],
    position: 'relative'
  }, isFullscreen && FullscreenStyles, isSidebar && SidebarStyles, isLightbox && Object.assign({}, LightboxStyles, {
    color: theme.color['text-core-on-fill-default']
  }), {
    display: 'flex',
    flexDirection: 'column'
  }, !isLightbox && _dialogWidth && {
    width: toPx(_dialogWidth)
  }, getDialogHeaderBackground({
    theme,
    use: modalUse,
    isConfirmDialog
  }), {
    '&': {
      [`@media only screen and (max-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
        height: '100%'
      }
    },
    [`& > form:first-child`]: {
      [`@media only screen and (max-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }
    },
    [`@media only screen and (min-width: ${ABOVE_MOBILE_BREAKPOINT})`]: {
      top: headerOverflow && toPx(headerOverflow)
    }
  }, isDefault && {
    // eslint-disable-next-line hubspot-dev/no-private-classes
    [`&.private-modal--default .uiDialogBody`]: {
      // Prevent the background image definition from leaking into the legacy component body
      // @see https://git.hubteam.com/HubSpot/foundations-components/issues/3817
      background: theme.color['fill-surface-default']
    }
  }, $cssStyles && $cssStyles);
});
export const Dialog = _ref => {
  let {
      centered = false,
      children,
      // className,
      use = 'default',
      headerCallback = emptyFunction,
      size = 'medium'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const classNames = getBackwardsCompatibilityClasses(use, size, children);
  const {
    domProps,
    tokenProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(DialogContextProvider, {
    value: {
      use,
      headerCallback
    },
    children: /*#__PURE__*/_jsx(StyledDialog, Object.assign({
      $centered: centered,
      $cssStyles: cssStyles,
      $size: size
    }, domProps, restProps, {
      className: classNames,
      children: children
    }))
  });
};